import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import "select2/dist/css/select2.min.css";
import "./HartuTimeSelectMulti.css";
import "select2";

export default function HartuTimeSelectMulti({
  availableHours,
  hoursLoading,
  lang,
  onChange,
  t,
  maxCapacity,
  hasCalendarRangeConsecutive
}) {
  const selectRef = useRef();
  const [selectedValues, setSelectedValues] = useState([]);
  const selectedValuesRef = useRef([]);
  const ignoreChangeRef = useRef(false);
  const capacitiesRef = useRef({});
  const [infoMessage, setInfoMessage] = useState("");

  // Función para validar que las horas seleccionadas sean consecutivas
  const areHoursConsecutive = (values) => {
    if (values.length <= 1) return true;

    // Extraer fecha y rangos de tiempo
    const parsedValues = values.map(v => {
      // Formato: "2026-05-14 08:00-10:00"
      const [date, timeRange] = v.split(" ");
      const [startTime, endTime] = timeRange.split("-");
      return { date, startTime, endTime };
    });

    // Verificar que todas las fechas sean iguales
    const firstDate = parsedValues[0].date;
    if (!parsedValues.every(p => p.date === firstDate)) {
      return false;
    }

    // Ordenar por hora de inicio
    parsedValues.sort((a, b) => a.startTime.localeCompare(b.startTime));

    // Verificar que las horas sean consecutivas (la hora final debe ser la hora inicial de la siguiente)
    for (let i = 1; i < parsedValues.length; i++) {
      if (parsedValues[i].startTime !== parsedValues[i - 1].endTime) {
        return false;
      }
    }

    return true;
  }; 

  const loadingText = t.loading_schedules?.[lang] || "Cargando horarios...";
  const placeholderText = t.select_a_timetable?.[lang] || "Selecciona horario...";

  useEffect(() => {
    if (hoursLoading || !availableHours?.length || !selectRef.current) return;

    const $select = $(selectRef.current);
    const capacitiesMap = {};

    if ($select.data("select2")) {
      $select.select2("destroy");
    }

    $select.empty();

    availableHours.forEach(([day, hours]) => {
      if (!Array.isArray(hours)) return;

      const $group = $("<optgroup>").attr("label", day);
      
      hours.forEach((hour) => {
        const text = `${day} ${hour.range} (${hour.capacity})`;
        const value = `${day} ${hour.range}`;

        const $option = $("<option>")
          .val(value)
          .text(text)
          .attr("data-capacity", hour.available_capacity);

        capacitiesMap[value] = hour.available_capacity;
        $group.append($option);
      });

      $select.append($group); 
    });

    capacitiesRef.current = capacitiesMap;

    // Inicializar Select2 (sin pasar `data`, para usar los <option> que creamos)
    $select.select2({
      placeholder: placeholderText,
      closeOnSelect: false,
      allowClear: true,
      width: "resolve",
      templateResult: (state) => {
        if (!state.id) return state.text;
        const isChecked = $select.val()?.includes(state.id.toString());
        return $(
          `<span><input type="checkbox" style="margin-right:8px;" ${
            isChecked ? "checked" : ""
          } />${state.text} </span>`
        );
      },
      templateSelection: (state) => state.text,
      minimumResultsForSearch: 5,
    });

    $select.on("select2:open", () => {
      $(".select2-results__option").each(function () {
        const text = $(this).text().trim();
        const match = $select
          .find("option")
          .filter(function () {
            return $(this).text().trim() === text;
          });
        if (match.length) {
          $(this).attr("data-capacity", match.data("capacity"));
        }
      });
    });

    $select.val(selectedValues).trigger("change.select2");

    $select.on("select2:select select2:unselect", function () {
      $(".select2-results__option[aria-selected]").each(function () {
        const selected = $(this).attr("aria-selected") === "true";
        $(this).find("input[type='checkbox']").prop("checked", selected);
      });
    });

    const handleChange = () => {
      if (ignoreChangeRef.current) {
        ignoreChangeRef.current = false;
        return;
      }

      const values = $select.val() || [];
      // Validar que las horas sean consecutivas
      if (hasCalendarRangeConsecutive && !areHoursConsecutive(values)) {
        setInfoMessage(
          t.consecutive_hours_required?.[lang] ||
          "Solo puedes seleccionar horas consecutivas"
        );
        setTimeout(() => {
          setInfoMessage("");
        }, 4000);

        const previousValues = selectedValuesRef.current;
        ignoreChangeRef.current = true;
        $select.val(previousValues).trigger("change.select2");
        return;
      }

      

      setSelectedValues(values);
      selectedValuesRef.current = values;

      if (values.length > 0) {
        const minCapacity = Math.min(...values.map(v => capacitiesRef.current[v]));
        maxCapacity(minCapacity);
      } else {
        maxCapacity(0);
      }

      onChange(values);
    };

    $select.on("change", handleChange);

    return () => {
      $select.select2("destroy");
    };
  }, [availableHours, hoursLoading, lang, placeholderText, selectedValues, onChange, maxCapacity, hasCalendarRangeConsecutive]);

  if (hoursLoading) {
    return (
      <div className="hartuTimeSelect__wrapper">
        <div className="hartuTimeSelect__loadingText">
          {loadingText}
        </div>
      </div>
    );
  }

  if (!availableHours?.length) {
    return null;
  }

  return (
    <>
      {infoMessage && (
        <div className="alert alert-danger" role="alert">
          <i className="fa-solid fa-circle-info me-2"></i>
          {infoMessage}
        </div>
      )}
      <div className="hartuTimeSelect__wrapper">
        <select ref={selectRef} multiple className="hoursSelect" />
      </div>
    </>
  );
}
